import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPatch, apiDel, apiUpload } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Info, AlertTriangle, CheckCircle2, XCircle, Trash2, Pencil, Upload, ShieldCheck } from 'lucide-react';
import {
  fmtDate, DaysBadge, StatusBadge, AiBadge, BTN_PRIMARY, BTN_SECONDARY, normalizeProjects,
} from './complianceShared';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const TABS = [
  { key: 'Labour License', testid: 'compliance-statutory-labour-tab' },
  { key: 'BOCW Registration', testid: 'compliance-statutory-bocw-tab' },
  { key: 'Pollution Certificate', testid: 'compliance-statutory-pollution-tab' },
];
const STATUSES = ['Required', 'Applied', 'Registered', 'Expired', 'Not Applicable'];
const LL_FORM_TYPES = ['Registration Certificate', 'Form 5', 'BOCW Certificate', 'NOC', 'Other'];
const POLLUTION_TYPES = ['NOC', 'CTO', 'CTE'];
const CATEGORIES = ['Private', 'State Govt', 'Central Govt'];

const EMPTY_FORM = {
  project_id: '', contract_category: '', form_type: '', registration_number: '',
  issuing_authority: '', issue_date: '', expiry_date: '', renewal_reminder_days: 60,
  status: 'Required', notes: '',
};

const suggestCategory = (project) => {
  const c = (project?.client || '').toLowerCase();
  if (c.includes('central')) return 'Central Govt';
  if (c.includes('state')) return 'State Govt';
  return 'Private';
};

export default function ComplianceStatutory() {
  const [tab, setTab] = useUrlTab('tab', 'Labour License');
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await apiGet('/compliance/statutory');
      setItems(r.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); apiGet('/projects').then(r => setProjects(normalizeProjects(r))).catch(() => {}); }, []);

  const tabItems = items.filter(i => i.statutory_type === tab);
  const llForProject = (pid) => items.find(i => i.statutory_type === 'Labour License' && i.project_id === pid);

  const alerts = tab !== 'Pollution Certificate' ? tabItems.filter(i => ['Required', 'Applied'].includes(i.status)) : [];
  const warnAlerts = alerts.filter(i => !i.threshold_met);
  const urgentAlerts = alerts.filter(i => i.threshold_met && i.status === 'Required');

  const openCreate = () => setModal({ mode: 'create', form: { ...EMPTY_FORM, form_type: tab === 'Pollution Certificate' ? 'NOC' : 'Registration Certificate' } });
  const openEdit = (i) => setModal({
    mode: 'edit', id: i.id,
    form: {
      project_id: i.project_id, contract_category: i.contract_category || '', form_type: i.form_type || '',
      registration_number: i.registration_number || '', issuing_authority: i.issuing_authority || '',
      issue_date: (i.issue_date || '').slice(0, 10), expiry_date: (i.expiry_date || '').slice(0, 10),
      renewal_reminder_days: i.renewal_reminder_days ?? 60, status: i.status || 'Required', notes: i.notes || '',
    },
  });

  const setForm = (k, v) => setModal(m => {
    const next = { ...m.form, [k]: v };
    if (k === 'project_id' && m.mode === 'create') {
      next.contract_category = suggestCategory(projects.find(p => p.id === v));
    }
    return { ...m, form: next };
  });

  const save = async () => {
    const f = modal.form;
    if (!f.project_id) { toast.error('Project is required'); return; }
    setSaving(true);
    try {
      const body = { ...f, statutory_type: tab, renewal_reminder_days: Number(f.renewal_reminder_days) || 60 };
      if (modal.mode === 'create') await apiPost('/compliance/statutory', body);
      else { delete body.project_id; await apiPatch(`/compliance/statutory/${modal.id}`, body); }
      toast.success(modal.mode === 'create' ? 'Entry added' : 'Entry updated');
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete this ${i.statutory_type} entry for ${i.project_name}?`)) return;
    try { await apiDel(`/compliance/statutory/${i.id}`); toast.success('Entry deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const confirmAi = async (i) => {
    try { await apiPatch(`/compliance/statutory/${i.id}`, { ai_suggested: false }); toast.success('Entry confirmed'); load(); }
    catch (e) { toast.error('Confirm failed'); }
  };

  const enforce = async (i) => {
    try { await apiPost(`/compliance/statutory/${i.id}/enforce`); toast.success('Entry enforced'); load(); }
    catch (e) { toast.error('Enforce failed'); }
  };

  const uploadDoc = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', i.project_id || '');
    try { await apiUpload(`/compliance/statutory/${i.id}/documents`, fd); toast.success('Document uploaded'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const isPollution = tab === 'Pollution Certificate';

  return (
    <div data-testid="compliance-statutory-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR · Compliance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Statutory Compliance</h1>
          <p className="text-sm text-slate-600 mt-1">Labour License · BOCW Registration · Pollution Certificates — project-wise statutory tracking.</p>
        </div>
        <button onClick={openCreate} className={`${BTN_PRIMARY} flex items-center gap-2`} data-testid="add-statutory-btn">
          <Plus size={14} /> Add Entry
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={t.testid}
                  className={`px-4 py-2 text-xs font-bold border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.key}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-sm text-[10px] ${tab === t.key ? 'bg-[#3b4872] text-white' : 'bg-slate-100 text-slate-500'}`}>
              {items.filter(i => i.statutory_type === t.key).length}
            </span>
          </button>
        ))}
      </div>

      {/* Smart Alerts */}
      {!isPollution && (warnAlerts.length > 0 || urgentAlerts.length > 0) && (
        <div className="mb-4 space-y-2" data-testid="statutory-smart-alerts">
          {urgentAlerts.map(i => (
            <div key={i.id} className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-sm px-4 py-3 text-xs text-rose-800" data-testid="smart-alert-urgent">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span><b>Project {i.project_name}:</b> Labour count ({i.current_labour_count}) has crossed the threshold ({i.labour_threshold}) — {tab} must be obtained immediately.</span>
            </div>
          ))}
          {warnAlerts.map(i => (
            <div key={i.id} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-800" data-testid="smart-alert-warning">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span><b>Project {i.project_name}:</b> Labour count ({i.current_labour_count}) has not reached threshold ({i.labour_threshold}) — {tab} application may not be required yet.</span>
            </div>
          ))}
        </div>
      )}

      {/* Info panels */}
      {tab === 'Labour License' && (
        <div className="mb-4 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-xs text-slate-600" data-testid="labour-license-info-panel">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span><b>Labour License Rules:</b> Private contracts → State Labour Dept (any count). State Govt contracts → State Labour Dept (min 50 workers). Central Govt contracts → Central Labour Dept (min 20 workers).</span>
        </div>
      )}
      {tab === 'BOCW Registration' && (
        <div className="mb-4 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-xs text-slate-600" data-testid="bocw-info-panel">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>BOCW registration requires the same labour count as the Labour License for the same project.</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="compliance-statutory-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-3 py-2">Project</th>
              {!isPollution && <th className="px-3 py-2">Contract Category</th>}
              {!isPollution && <th className="px-3 py-2 text-right">Threshold</th>}
              {!isPollution && <th className="px-3 py-2 text-right">Labour Count</th>}
              {!isPollution && <th className="px-3 py-2 text-center">Threshold Met</th>}
              <th className="px-3 py-2">{isPollution ? 'Certificate Type' : 'Form Type'}</th>
              <th className="px-3 py-2">{isPollution ? 'Issuing Authority' : 'Reg. Number'}</th>
              {isPollution && <th className="px-3 py-2">Issue Date</th>}
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Expiry</th>
              <th className="px-3 py-2">Renewal</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tabItems.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-sm text-slate-400">No {tab} entries yet. Click “Add Entry” to create one.</td></tr>
            )}
            {tabItems.map(i => (
              <tr key={i.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`statutory-row-${i.id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">
                  {i.project_name || i.project_id}
                  {i.ai_suggested && <div className="mt-1"><AiBadge /></div>}
                  {(i.documents || []).length > 0 && <div className="text-[10px] text-slate-400 mt-0.5">{i.documents.length} doc(s)</div>}
                </td>
                {!isPollution && <td className="px-3 py-2">{i.contract_category}</td>}
                {!isPollution && <td className="px-3 py-2 text-right">{i.labour_threshold}</td>}
                {!isPollution && <td className="px-3 py-2 text-right font-semibold">{i.current_labour_count}</td>}
                {!isPollution && (
                  <td className="px-3 py-2 text-center">
                    {i.threshold_met
                      ? <CheckCircle2 size={16} className="inline text-emerald-600" data-testid="threshold-met-tick" />
                      : <XCircle size={16} className="inline text-rose-500" data-testid="threshold-not-met-cross" />}
                  </td>
                )}
                <td className="px-3 py-2">{i.form_type || '—'}</td>
                <td className="px-3 py-2">{isPollution ? (i.issuing_authority || '—') : (i.registration_number || '—')}</td>
                {isPollution && <td className="px-3 py-2">{fmtDate(i.issue_date)}</td>}
                <td className="px-3 py-2"><StatusBadge status={i.status} /></td>
                <td className="px-3 py-2">{fmtDate(i.expiry_date)}</td>
                <td className="px-3 py-2"><DaysBadge days={i.days_remaining} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {i.ai_suggested && (
                    <button onClick={() => confirmAi(i)} className="text-emerald-700 hover:bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm text-[10px] font-bold mr-1" data-testid="confirm-ai-statutory-btn">Confirm</button>
                  )}
                  {!i.enforced && (
                    <button onClick={() => enforce(i)} className="text-[#3b4872] hover:bg-slate-100 border border-slate-300 px-2 py-1 rounded-sm text-[10px] font-bold mr-1" data-testid="enforce-statutory-btn">
                      <ShieldCheck size={11} className="inline -mt-0.5 mr-0.5" />Enforce
                    </button>
                  )}
                  <label className="text-slate-500 hover:text-[#3b4872] p-1 cursor-pointer inline-block" data-testid="upload-statutory-doc-label">
                    <Upload size={13} />
                    <input type="file" className="hidden" onChange={e => uploadDoc(i, e.target.files?.[0])} />
                  </label>
                  <button onClick={() => openEdit(i)} className="text-slate-500 hover:text-[#3b4872] p-1" data-testid="edit-statutory-btn"><Pencil size={13} /></button>
                  <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600 p-1" data-testid="delete-statutory-btn"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-sm border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="statutory-modal">
            <h2 className="font-display text-lg font-bold mb-4">{modal.mode === 'create' ? `Add ${tab} Entry` : `Edit ${tab} Entry`}</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2 block"><span className="kpi-overline">Project</span>
                <select value={modal.form.project_id} onChange={e => setForm('project_id', e.target.value)} disabled={modal.mode === 'edit'}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="statutory-project-select">
                  <option value="">— Select project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>

              {tab === 'BOCW Registration' && modal.form.project_id && (
                llForProject(modal.form.project_id) ? (
                  <div className="col-span-2 bg-indigo-50 border border-indigo-200 rounded-sm px-3 py-2 text-xs text-indigo-800" data-testid="bocw-linked-ll-card">
                    <b>Linked Labour License:</b> {llForProject(modal.form.project_id).registration_number || 'No reg. number'} · Status: {llForProject(modal.form.project_id).status} · Expiry: {fmtDate(llForProject(modal.form.project_id).expiry_date)}
                  </div>
                ) : (
                  <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-500" data-testid="bocw-no-ll-card">
                    No Labour License entry exists yet for this project.
                  </div>
                )
              )}

              {!isPollution && (
                <label className="block"><span className="kpi-overline">Contract Category</span>
                  <select value={modal.form.contract_category} onChange={e => setForm('contract_category', e.target.value)}
                          className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="contract-category-select">
                    <option value="">— Auto from project —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></label>
              )}
              <label className="block"><span className="kpi-overline">{isPollution ? 'Certificate Type' : 'Form Type'}</span>
                <select value={modal.form.form_type} onChange={e => setForm('form_type', e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="form-type-select">
                  {(isPollution ? POLLUTION_TYPES : LL_FORM_TYPES).map(f => <option key={f} value={f}>{f}</option>)}
                </select></label>
              <label className="block"><span className="kpi-overline">Registration Number</span>
                <input value={modal.form.registration_number} onChange={e => setForm('registration_number', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="registration-number-input" /></label>
              <label className="block"><span className="kpi-overline">Issuing Authority</span>
                <input value={modal.form.issuing_authority} onChange={e => setForm('issuing_authority', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="issuing-authority-input" /></label>
              <label className="block"><span className="kpi-overline">Issue Date</span>
                <input type="date" value={modal.form.issue_date} onChange={e => setForm('issue_date', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="issue-date-input" /></label>
              <label className="block"><span className="kpi-overline">Expiry Date</span>
                <input type="date" value={modal.form.expiry_date} onChange={e => setForm('expiry_date', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="statutory-expiry-input" /></label>
              <label className="block"><span className="kpi-overline">Reminder Days</span>
                <input type="number" value={modal.form.renewal_reminder_days} onChange={e => setForm('renewal_reminder_days', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="statutory-reminder-days-input" /></label>
              <label className="block"><span className="kpi-overline">Status</span>
                <select value={modal.form.status} onChange={e => setForm('status', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="statutory-status-select">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label className="col-span-2 block"><span className="kpi-overline">Notes</span>
                <textarea value={modal.form.notes} onChange={e => setForm('notes', e.target.value)} rows={2} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="statutory-notes-input" /></label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setModal(null)} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="save-statutory-btn">{saving ? 'Saving…' : 'Save Entry'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
